import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "@supabase/auth-helpers-react";
import LogoBlue from "../../assets/HD-Horizontal-Blue.png";
import HDLoadingButton from "@/common/components/HDLoadingButton";
import Spinner from "@/common/components/Spinner";
import { apiFetch, expectJsonBody, getEffectiveSession } from "@/common/lib/api";
import { fetchAccountResolvedEnvelope, getDefaultView } from "@/common/lib/supabaseClient";
import {
    setStoredSelectedClinicAccountId,
    clearStoredSelectedClinicAccountId,
} from "@/common/lib/selectedClinicAccountStorage";
import { useGroupSession } from "@/state/hooks/useGroupSession";
import { deriveGroupMembership } from "@/common/lib/groupUsageApi";
type ClinicSummary = { id: string; clinic_name: string | null; clinic_location: string | null };

/**
 * Shown when the signed-in user has more than one clinic membership and must choose which to open.
 */
export default function SelectClinic() {
    const session = useSession();
    const navigate = useNavigate();
    const [clinics, setClinics] = useState<ClinicSummary[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [choosingId, setChoosingId] = useState<string | null>(null);

    // Corporate group members belong on /corporate, never the clinic picker (v1 rule, mirrors
    // PermissionWrapper). This route renders OUTSIDE PermissionWrapper, so its group→/corporate
    // guard doesn't cover it — a group member reaching /select-clinic directly would otherwise get
    // the picker. Wait for the group session to settle so a normal multi-clinic user isn't misrouted
    // during load (deriveGroupMembership is false while loading and on the expected 401).
    const groupSession = useGroupSession();
    const { isLoading: groupLoading } = groupSession;
    const { isGroupMember } = deriveGroupMembership(groupSession);
    useEffect(() => {
        if (!session?.user || groupLoading) {
            return;
        }
        if (isGroupMember) {
            navigate("/corporate", { replace: true });
        }
    }, [session?.user?.id, groupLoading, isGroupMember, navigate]);

    useEffect(() => {
        // Wait for the group session to settle before fetching clinics or rendering the picker:
        // deriveGroupMembership stays false while groupMe is in flight, so a corporate member could
        // otherwise briefly see (or act on) "Choose a clinic" before the /corporate redirect fires.
        // Group members are redirected, so we never fetch their clinic summaries at all.
        if (!session?.user || groupLoading || isGroupMember) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = expectJsonBody(
                    await apiFetch<{ clinics: ClinicSummary[] }>("account/clinic-summaries"),
                    "account/clinic-summaries",
                );
                if (cancelled) {
                    return;
                }
                const list = data.clinics ?? [];
                if (list.length <= 1) {
                    navigate("/", { replace: true });
                    return;
                }
                setClinics(list);
            } catch {
                if (!cancelled) {
                    setError("We could not load your clinics. Try again or contact support.");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [session?.user?.id, groupLoading, isGroupMember, navigate]);

    async function chooseClinic(accountId: string) {
        const eff = await getEffectiveSession();
        if (!eff?.userId) {
            setError("Session expired. Please sign in again.");
            return;
        }
        setChoosingId(accountId);
        setError(null);
        try {
            setStoredSelectedClinicAccountId(eff.userId, accountId);
            const resolved = await fetchAccountResolvedEnvelope(eff.userId);
            if (!resolved) {
                throw new Error("Failed to fetch account data");
            }
            if (!resolved.account && resolved.no_access_reason === "account_users_disabled") {
                clearStoredSelectedClinicAccountId(eff.userId);
                setChoosingId(null);
                setError("Team access is disabled for this clinic. Contact the clinic owner.");
                return;
            }
            const defaultView =
                resolved?.account?.meta_data?.default_view ?? (await getDefaultView(eff.userId, accountId));
            const url = new URL(window.location.origin);
            url.searchParams.set("page", defaultView);
            window.location.assign(url.pathname + url.search);
        } catch {
            clearStoredSelectedClinicAccountId(eff.userId);
            setChoosingId(null);
            setError("Could not open that clinic. Try again.");
        }
    }

    if (!session?.user || groupLoading || isGroupMember) {
        // Group session still resolving, or (isGroupMember) the effect above is redirecting a member
        // to /corporate — show a spinner rather than flashing "Choose a clinic" in the interim.
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (clinics === null && !error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6">
                <Spinner />
                <p className="text-sm text-gray-600">Loading your clinics…</p>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-1 flex-col justify-center px-6 py-12 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <img className="mx-auto h-24 w-auto" src={LogoBlue} alt="HappyDoc" />
                <h1 className="mt-10 text-center text-xl font-semibold leading-9 tracking-tight text-gray-900">
                    Choose a clinic
                </h1>
                <p className="mt-2 text-center text-sm text-gray-600">
                    Your account is linked to more than one clinic.
                </p>
                <p className="mt-2 text-center text-sm text-gray-600">Select which one to use.</p>
            </div>

            <div className="mx-auto mt-10 w-full max-w-md space-y-3">
                {error && (
                    <p className="rounded-md bg-red-50 px-3 py-2 text-center text-sm text-red-800" role="alert">
                        {error}
                    </p>
                )}
                {clinics?.map((c) => {
                    const title = c.clinic_name?.trim() || "Unnamed clinic";
                    const sub = c.clinic_location?.trim();
                    return (
                        <div
                            key={c.id}
                            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-max-300"
                        >
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="font-medium text-gray-900">{title}</p>
                                    {sub ? <p className="text-sm text-gray-500">{sub}</p> : null}
                                </div>
                                <HDLoadingButton
                                    type="button"
                                    className="mt-3 w-full shrink-0 sm:mt-0 sm:w-auto"
                                    loading={choosingId === c.id}
                                    disabled={choosingId != null && choosingId !== c.id}
                                    onClick={() => void chooseClinic(c.id)}
                                >
                                    Continue
                                </HDLoadingButton>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
